import { supabase } from '../supabase';
import { authManager } from '../authManager';

// Simple cache for reference data that doesn't change often
class ReferenceDataCache {
  private cache = new Map<string, { data: any; timestamp: number; ttl: number }>();
  // Dedup delle richieste in volo (stessa chiave richiesta N volte prima
  // che la prima risposta arrivi = 1 sola fetch)
  private inFlight = new Map<string, Promise<any[]>>();
  private readonly DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes

  private getCacheKey(table: string, organizationId: string, filters?: string): string {
    return `${table}_${organizationId}_${filters || 'all'}`;
  }

  private isExpired(item: { timestamp: number; ttl: number }): boolean {
    return Date.now() - item.timestamp > item.ttl;
  }

  async get<T>(
    table: string,
    select: string,
    organizationId: string,
    additionalFilters?: Record<string, any>,
    ttl = this.DEFAULT_TTL,
    orderBy?: { column: string; ascending?: boolean }
  ): Promise<T[]> {
    const filterStr = additionalFilters ? JSON.stringify(additionalFilters) : '';
    const orderStr = orderBy ? JSON.stringify(orderBy) : '';
    const key = this.getCacheKey(table, organizationId, `${filterStr}_${orderStr}`);

    // Check cache first
    const cached = this.cache.get(key);
    if (cached && !this.isExpired(cached)) {
      return cached.data;
    }

    const pending = this.inFlight.get(key);
    if (pending) return pending as Promise<T[]>;

    const promise = (async () => {
      let query = supabase
        .from(table)
        .select(select)
        .eq('organization_id', organizationId);

      // Apply additional filters
      if (additionalFilters) {
        Object.entries(additionalFilters).forEach(([key, value]) => {
          query = query.eq(key, value);
        });
      }

      // Apply ordering
      if (orderBy) {
        query = query.order(orderBy.column, { ascending: orderBy.ascending ?? true });
      }

      const { data, error } = await query;

      if (error) {
        return [];
      }

      const result = data || [];

      this.cache.set(key, {
        data: result,
        timestamp: Date.now(),
        ttl
      });

      return result;
    })();

    this.inFlight.set(key, promise);
    try {
      return (await promise) as T[];
    } finally {
      this.inFlight.delete(key);
    }
  }

  // Clear cache for specific table or all
  clear(table?: string, organizationId?: string): void {
    if (table && organizationId) {
      // Clear specific table for organization
      const keysToDelete = Array.from(this.cache.keys()).filter(key =>
        key.startsWith(`${table}_${organizationId}`)
      );
      keysToDelete.forEach(key => this.cache.delete(key));
    } else {
      // Clear all cache
      this.cache.clear();
    }
  }

  // Get cache stats for debugging
  getStats() {
    const now = Date.now();
    const entries = Array.from(this.cache.entries());

    return {
      totalEntries: entries.length,
      validEntries: entries.filter(([_, item]) => !this.isExpired(item)).length,
      expiredEntries: entries.filter(([_, item]) => this.isExpired(item)).length,
      entries: entries.map(([key, item]) => ({
        key,
        isExpired: this.isExpired(item),
        ageMs: now - item.timestamp,
        ttlMs: item.ttl
      }))
    };
  }
}

// Export singleton instance
export const referenceCache = new ReferenceDataCache();

// User profile cache to avoid duplicate organization_id lookups
class UserProfileCache {
  private profileCache = new Map<string, { organization_id: string; timestamp: number }>();
  // Dedup delle richieste in volo: al primo load N chiamate concorrenti
  // bucherebbero tutte la cache e sparerebbero N fetch identiche
  private inFlight = new Map<string, Promise<string>>();
  private readonly TTL = 10 * 60 * 1000; // 10 minutes

  private isExpired(item: { timestamp: number }): boolean {
    return Date.now() - item.timestamp > this.TTL;
  }

  async getOrganizationId(userId: string): Promise<string> {
    // Check cache first
    const cached = this.profileCache.get(userId);
    if (cached && !this.isExpired(cached)) {
      return cached.organization_id;
    }

    const pending = this.inFlight.get(userId);
    if (pending) return pending;

    const promise = (async () => {
      const { data: userProfile, error } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userId)
        .single();

      if (error || !userProfile?.organization_id) {
        throw new Error('User profile not found or not associated with an organization');
      }

      this.profileCache.set(userId, {
        organization_id: userProfile.organization_id,
        timestamp: Date.now()
      });

      return userProfile.organization_id;
    })();

    this.inFlight.set(userId, promise);
    try {
      return await promise;
    } finally {
      this.inFlight.delete(userId);
    }
  }

  // Clear cache for specific user or all
  clear(userId?: string): void {
    if (userId) {
      this.profileCache.delete(userId);
    } else {
      this.profileCache.clear();
    }
  }
}

// Export singleton instance
export const userProfileCache = new UserProfileCache();

// Helper function to get current user's organization ID with caching
export async function getCurrentUserOrganizationId(): Promise<string> {
  const user = await authManager.getUser();
  if (!user) {
    throw new Error('User not authenticated');
  }
  return userProfileCache.getOrganizationId(user.id);
}

// Helper functions for commonly used reference data
export async function getCachedClientStates(organizationId: string) {
  return referenceCache.get(
    'client_states',
    'id, organization_id, name, description, color, is_default, is_active, is_lead, sort_order, created_at, updated_at',
    organizationId,
    undefined,
    undefined,
    { column: 'sort_order', ascending: true }
  );
}

export async function getCachedLeadSources(organizationId: string) {
  return referenceCache.get(
    'lead_sources',
    'id, organization_id, name, description, is_active, sort_order, created_at, updated_at',
    organizationId,
    { is_active: true },
    undefined,
    { column: 'sort_order', ascending: true }
  );
}

export async function getCachedLeadStates(organizationId: string) {
  return referenceCache.get(
    'lead_states',
    'id, organization_id, name, description, color, is_default, is_active, is_won, is_lost, sort_order, created_at, updated_at',
    organizationId,
    { is_active: true },
    undefined,
    { column: 'sort_order', ascending: true }
  );
}